import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetFormattedRewardCatalog } from '@/models/RewardCatalogModel';
import { useDataTable } from '@/hooks/useDataTable-hook';
import { getRewardCatalogCsvFile, getRewardCatalog, downloadTemplate, handleBulkUploadAPI } from '@/services/rewardCatalog';
import { RewardCatalogListColConfig } from '@/services/MockData/RewardCatalogList/RewardCatalogListColConfig';
import { useQueries } from '@tanstack/react-query';
import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils';
import { DROPDOWN_TYPES } from '@/utils/constants/dropdownConstants';
import { useDropdown } from './Dropdown-hook';
import { Constants, FILE_UPLOAD_TEMPLATE_TYPE } from '@/utils/constants/commonConstants';
import {useAppDispatch, useAppSelector} from '@/store'
import {
  setOpenUpload as setOpenUploadStore,
  setUploadProgress as setUploadProgressStore,
  setUploadErrorMessage as setUploadErrorMessageStore,
  setUploading as setUploadingStore,
  setSuccessfullyUploaded as setSuccessfullyUploadedStore,
  setFormData as setFormDataStore,
  selectOpenUpload,
} from '@/store/slices/rewardBulkUpload'
import { customRules, customRulesRewards } from '@/services/MockData/BulkUpload/customRules';

export const useRewardCatalog = () => {
  const dispatch = useAppDispatch()
  const { setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr, handleResetAll } = useDataTable();
  const { dropdownList } = useDropdown({ dropdownType: DROPDOWN_TYPES.RewardsCatalogAction })

  const progressInterval = 200
  const simulatedProgressMax = 90
  const simulatedProgressIncrement = 5
  const [openUpload, setOpenUploadLocal] = useState(false)
  const [formData, setFormDataLocal] = useState(null)
  const [uploadProgress, setUploadProgressLocal] = useState(0)
  const [uploading, setUploadingLocal] = useState(false)
  const [successfullyUploaded, setSuccessfullyUploadedLocal] = useState(false)
  const [uploadErrorMessage, setUploadErrorMessageLocal] = useState('')

  const setOpenUpload = (value) => {
    setOpenUploadLocal(value)
    dispatch(setOpenUploadStore(value))
  }

  const setFormData = (value) => {
    setFormDataLocal(value)
    dispatch(setFormDataStore(value))
  }

  const setUploadProgress = (value) => {
    setUploadProgressLocal(value)
    dispatch(setUploadProgressStore(value))
  }

  const setUploading = (value) => {
    setUploadingLocal(value)
    dispatch(setUploadingStore(value))
    if(value === true && uploadErrorMessage){
       setUploadErrorMessage('')
    }
  }

  const setSuccessfullyUploaded = (value) => {
    setSuccessfullyUploadedLocal(value)
    dispatch(setSuccessfullyUploadedStore(value))
  }

  const setUploadErrorMessage = (value) => {
    setUploadErrorMessageLocal(value)
    dispatch(setUploadErrorMessageStore(value))
  }

  // Get Redux state to sync with local state
  const reduxOpenUpload = useAppSelector(selectOpenUpload)

  // Sync Redux state with local state when Redux state changes from other components
  useEffect(() => {
    if(reduxOpenUpload){
      setOpenUploadLocal(reduxOpenUpload)
    }
  }, [reduxOpenUpload])

  // Helper to build query params, optionally including pagination
  const getQueryParams = useCallback(({ includePagination = true } = {}) => {
    const params = new URLSearchParams();

    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column);
      params.append('order', sortingObj.direction);
    }

    if (includePagination) {
      params.append('pageSize', 10);
      params.append('page', pageIndex || 0);
    }

    return params.toString();
  }, [sortingObj?.column, sortingObj?.direction, pageIndex]);

  const queryParams = useMemo(() => getQueryParams({ includePagination: true }), [getQueryParams]);

  const [{ data, isLoading, isFetching }] = useQueries({
    queries: [
      {
        queryKey: ['REWARD_CATALOG', queryParams, searchArr],
        queryFn: () => getRewardCatalog(queryParams, searchArr),
      },
    ],
  });

  const resultsData = useMemo(() => data || {}, [data]);
  const rewardCatalogData = useMemo(() => GetFormattedRewardCatalog(resultsData), [resultsData]);

  const onDownloadBtnClick = async (selectedIds) => {
    const requestBody = {
      ids: selectedIds,
      filters: searchArr,
    };

    try {
      // Exclude pagination params for download
      const downloadQueryParams = getQueryParams({ includePagination: false });
      const data = await getRewardCatalogCsvFile(downloadQueryParams, requestBody);
      const fileName = `RewardCatalog_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      console.error('Error downloading CSV file:', error);
    }
  };

  // Extract sortBy and order from API response and sync with sortingObj
  useEffect(() => {
    if (resultsData && resultsData.sortBy && resultsData.order) {
      // Only update if different to avoid infinite loop
      if (resultsData.sortBy !== sortingObj?.column || resultsData.order !== sortingObj?.direction) {
        setSortingObj({column: resultsData.sortBy, direction: resultsData.order})
      }
    }
  }, [resultsData, sortingObj, setSortingObj])

  const handleDownloadTemplate = async (uploadFor) => {
    try {
      if (uploadFor === Constants.BulkRewards) {
        const response = await downloadTemplate();
        if (!response) return
        const fileName = `BulkUploadRewardTemplate_${getDateWithTimeString()}`
        downloadCSVFiles(response, fileName)
      }
    } catch (error) {
      console.error('Error downloading CSV file:', error);
    }
  };

  const handleCloseNotification = () => {
    setOpenUpload(false)
  };

  const handleBulkUpload = async (file, uploadFor) => {
    const requestBody = {
      fileUrl: file?.storeList,
      templateType: uploadFor === Constants.BulkDigitalCodes ? FILE_UPLOAD_TEMPLATE_TYPE.DIGITAL_CODES : FILE_UPLOAD_TEMPLATE_TYPE.REWARD_CATALOG
    };

    setUploading(true)
    const stopProgress = simulateProgress()

    try {
      const response = await handleBulkUploadAPI(queryParams, requestBody);

      setUploadProgress(100)
      if (response?.success) {
        if (response?.data?.validationErrors && response?.data?.validationErrors?.length > 0) {
          setUploadErrorMessage(response)
          setFormData(null)
          return false
        } else {
          setOpenUpload(false)
          setFormData(null)
          setUploadProgress(0)
          setUploading(false)
          setUploadErrorMessage('')
          setSuccessfullyUploaded(true)
          setSortingObj({ sortBy: "updatedDate", order: "desc" })
          setTimeout(() => {
            setSuccessfullyUploaded(false)
          }, 30000);
          return true
        }
      } else {
        const errorMsg = response?.message
        setUploadErrorMessage(errorMsg)
        return false
      }
    } catch (error) {
      setFormData((prev) => { return {...prev, targetMediaFile: [], storeList: null }})
      setUploadErrorMessage(error?.cause || error?.message)
      return false
    } finally {
      setUploading(false)
      stopProgress()
    }
  };

  const simulateProgress = () => {
    setUploadProgress(0)
    const interval = setInterval(() => {
      setUploadProgress(currentProgress => {
        const newProgress = currentProgress + simulatedProgressIncrement
        if (newProgress >= simulatedProgressMax) {
          clearInterval(interval)
          return simulatedProgressMax
        }
        return newProgress
      })
    }, progressInterval)

    return () => clearInterval(interval)
  }

  const handleCloseModal = () => {
    setOpenUpload(false)
    setFormData(null)
    setUploadProgress(0)
    setUploading(false)
    setSuccessfullyUploaded(false)
    setUploadErrorMessage('')
  }

  return {
    rewardCatalogData,
    rewardCatalogColConfigData: RewardCatalogListColConfig,
    isLoading,
    isFetching,
    customRules,
    customRulesRewards,
    uploadProgress,
    uploading,
    successfullyUploaded,
    uploadErrorMessage,
    formData,
    setFormData,
    openUpload,
    setOpenUpload,
    setUploadProgress,
    setUploading,
    setSuccessfullyUploaded,
    setUploadErrorMessage,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll,
    onDownloadBtnClick,
    dropdownList,
    handleDownloadTemplate,
    handleBulkUpload,
    handleCloseNotification,
    handleCloseModal,
  };
};
